import React, { useCallback, useEffect, useState } from 'react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import Link from 'components/commercetools-ui/atoms/link';
import { Reference } from 'types/reference';

export interface Props {
  barId?: string;
  text: string;
  linkSubstring?: string;
  boldSubstring?: string;
  target?: Reference;
  color: 'tertiary' | 'inverted';
  allowClose?: boolean;
  visible: boolean;
  onClose?: () => void;
  onVisibilityChange?: () => void;
}

const AnnouncementBar: React.FC<Props> = ({
  text,
  linkSubstring,
  boldSubstring,
  target,
  allowClose = false,
  color = 'tertiary',
  visible,
  onClose,
  onVisibilityChange,
}) => {
  const [previewText, setPreviewText] = useState({ prefix: text, middle: '', suffix: '' });

  const processHighlighting = useCallback(() => {
    const highlightedText = linkSubstring || boldSubstring;
    if (!highlightedText) return;

    const start = text?.indexOf(highlightedText);

    if (!highlightedText || start === -1) {
      setPreviewText({ prefix: text, middle: '', suffix: '' });
      return;
    }

    const end = start + highlightedText.length - 1;

    setPreviewText({
      prefix: text.substring(0, start),
      middle: text.substring(start, end + 1),
      suffix: text.substring(end + 1),
    });
  }, [text, linkSubstring, boldSubstring]);

  useEffect(() => {
    processHighlighting();
  }, [processHighlighting]);

  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange();
    }
  }, [visible, onVisibilityChange]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div
      className={`relative flex min-h-42 w-full items-center ${color === 'tertiary' ? 'bg-tertiary' : 'bg-inverted'}`}
    >
      <div
        className={`flex size-full items-center gap-x-24 ${
          allowClose ? 'justify-between' : 'justify-center'
        } container py-16`}
      >
        {allowClose && <div className="absolute md:static" />}
        <p className="text-inverted text-wrap text-left text-14 leading-loose md:text-center">
          {previewText.prefix}
          {linkSubstring && target ? (
            <Link link={target} className="underline underline-offset-2">
              {previewText.middle}
            </Link>
          ) : (
            <span className="font-bold">{previewText.middle}</span>
          )}
          {previewText.suffix}
        </p>
        {allowClose && (
          <button
            className={color === 'tertiary' ? 'focus:outline-primary-black focus-visible:outline-primary-black' : ''}
            onClick={handleClose}
          >
            <CloseIcon className="size-24 cursor-pointer fill-text-inverted" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AnnouncementBar;
